import { OriginKeyword } from '@/micro/types/register.type'
import { getQueryParameterByName } from './urls'
import { decodeJWT } from './jwt'
import { PRODUCT_NAME_MAPPING } from '@/micro/modules/version'
import { ApplicationVersion } from '@/micro/types/version.type'
import { Product } from '@/micro/types/product.type'

const URL_BASE = 'https://www.alegra.com'

const PRODUCT_TYPES = {
  POS: 'POS',
  TPV: 'TPV',
  PUNTO_VENTA: 'PUNTO-VENTA',
  FACTURACION: 'AF',
  CONTABILIDAD: 'AC',
  NOMINA: 'AN',
} as const

const URL_VERSION_SLUG: Record<ApplicationVersion, string> = {
  argentina: 'argentina',
  colombia: 'colombia',
  costaRica: 'costarica',
  spain: 'es',
  mexico: 'mexico',
  panama: 'panama',
  peru: 'peru',
  republicaDominicana: 'rdominicana',
  venezuela: 'venezuela',
} as const

const POS_ORIGINS: OriginKeyword[] = ['alegra-pos', 'partner-pos-wompi', 'pos-boutiques']

const getOriginKeywordFromPasswordResetToken = (): OriginKeyword | null => {
  const currentPath = window.location.pathname
  const passwordResetPattern = /^\/user\/password-reset\/key\/(.+)$/
  const match = currentPath.match(passwordResetPattern)
  
  if (match && match[1]) {
    try {
      const token = match[1]
      const payload = decodeJWT<{ originKeyword?: OriginKeyword }>(token)
      return payload.originKeyword || null
    } catch (error) {
      return null
    }
  }
  
  return null
}

const isPOSProduct = (originKeyword: OriginKeyword): boolean => {
  const tokenOrigin = getOriginKeywordFromPasswordResetToken()
  if (tokenOrigin) {
    return POS_ORIGINS.includes(tokenOrigin)
  }
  
  return POS_ORIGINS.includes(originKeyword) || getQueryParameterByName('solution') === 'alegra-pos'
}

const isFacturacionProduct = (originKeyword: OriginKeyword): boolean => {
  return originKeyword === 'alegra-only-invoicing'
}

const isPayrollProduct = (originKeyword: OriginKeyword): boolean => {
  const tokenOrigin = getOriginKeywordFromPasswordResetToken()
  if (tokenOrigin) {
    return tokenOrigin === 'alegra-payroll'
  }
  
  return originKeyword === 'alegra-payroll' || getQueryParameterByName('solution') === 'alegra-ne'
}

const getPOSInfo = ({ version }: { version: ApplicationVersion }): { product: 'POS' | 'TPV' | 'PUNTO_VENTA'; logo: string; name: string } => {
  const urlArray = window.location.pathname.split('/')
  if (urlArray.includes('tpv')) return { product: 'TPV', logo: 'alegra-tpv', name: 'Alegra TPV' }
  if (urlArray.includes('punto-venta'))
    return { product: 'PUNTO_VENTA', logo: 'alegra-punto-venta', name: 'Alegra Punto de Venta' }
  if (version === 'argentina') return { product: 'POS', logo: 'alegra-punto-venta', name: 'Alegra POS' }
  return { product: 'POS', logo: 'alegra-pos', name: 'Alegra POS' }
}

const getPOSUrl = (applicationVersion: ApplicationVersion): string => {
  switch (applicationVersion) {
    case 'argentina':
    case 'peru':
      return `${URL_BASE}/${URL_VERSION_SLUG[applicationVersion]}/punto-venta`
    
    case 'spain':
      return `${URL_BASE}/${URL_VERSION_SLUG[applicationVersion]}/tpv`
    
    default:
      return `${URL_BASE}/${URL_VERSION_SLUG[applicationVersion]}/pos`
  }
}

const getProductInfo = (
  applicationVersion: ApplicationVersion,
  originKeyword: OriginKeyword
): { product: Product; logo: string; name: string, url: string } => {
  if (isPOSProduct(originKeyword)) {
    const posInfo = getPOSInfo({ version: applicationVersion })
    return {
      product: PRODUCT_TYPES[posInfo.product],
      logo: posInfo.logo,
      name: posInfo.name,
      url: getPOSUrl(applicationVersion),
    }
  }

  if (isFacturacionProduct(originKeyword)) {
    return {
      product: PRODUCT_TYPES.FACTURACION,
      logo: 'alegra-facturacion',
      name: 'Alegra Facturación',
      url: URL_BASE,
    }
  }

  if (isPayrollProduct(originKeyword)) {
    const payrollUrl = payrollValidCountries.includes(applicationVersion) ? `${URL_BASE}/${URL_VERSION_SLUG[applicationVersion]}/nomina` : URL_BASE
    return {
      product: PRODUCT_TYPES.NOMINA,
      logo: 'alegra-nomina',
      name: 'Alegra Nómina',
      url: payrollUrl,
    }
  }

  return {
    product: PRODUCT_TYPES.CONTABILIDAD,
    logo: 'alegra',
    name: PRODUCT_NAME_MAPPING[applicationVersion as keyof typeof PRODUCT_NAME_MAPPING],
    url: URL_BASE,
  }
}

// NOTE: These countries are valid for payroll products, if the product supports other countries, add them to the list
const payrollValidCountries: ApplicationVersion[] = [
  'colombia',
  'republicaDominicana',
]

export { isPOSProduct, isFacturacionProduct, getProductInfo, payrollValidCountries }
